import { ArrowLeftRight } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// Services & Projects — Revenue Recognition Page
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  DollarSign, Plus, Search, Eye, FileText,
  AlertCircle, RefreshCw, Calendar, TrendingUp, PieChart, BarChart3, Zap, Shield
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { formatINR } from '@/shared/utils/currency';
import { useRevenueRecognition, useProjects, useJournalEntries } from '../hooks/useServicesProjectsApi';

const RECOGNITION_METHODS = [
  { value: 'milestone_based', label: 'Milestone Based' },
  { value: 'time_material', label: 'Approved Timesheets' },
  { value: 'straight_line', label: 'Straight Line' },
  { value: 'manual_schedule', label: 'Manual Schedule' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'subscription', label: 'Subscription' },
];

const RECOGNITION_STATUS = [
  { value: 'pending', label: 'Pending', color: 'bg-slate-100 text-slate-700' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
  { value: 'recognized', label: 'Recognized', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'reversed', label: 'Reversed', color: 'bg-red-100 text-red-800' },
];

const JOURNAL_STATUS = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'posted', label: 'Posted', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'reversed', label: 'Reversed', color: 'bg-red-100 text-red-800' },
];

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

function getStatusConfig(status: string) {
  return RECOGNITION_STATUS.find(s => s.value === status) || { label: status, color: 'bg-slate-100 text-slate-700' };
}

function getJournalStatusConfig(status: string) {
  return JOURNAL_STATUS.find(s => s.value === status) || { label: status, color: 'bg-slate-100 text-slate-700' };
}

export function RevenueRecognitionPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'schedules' | 'entries' | 'journals'>('overview');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [running, setRunning] = useState(false);

  const { data: rawRecognitions = [], isLoading: recognitionsLoading } = useRevenueRecognition();
  const { data: rawProjects = [] } = useProjects({ status: 'active' });
  const { data: rawJournalEntries = [] } = useJournalEntries({});

  const recognitions = useMemo(() => Array.isArray(rawRecognitions) ? rawRecognitions : [], [rawRecognitions]);
  const projects = useMemo(() => Array.isArray(rawProjects) ? rawProjects : [], [rawProjects]);
  const journalEntries = useMemo(() => Array.isArray(rawJournalEntries) ? rawJournalEntries : [], [rawJournalEntries]);

  // Filter recognitions
  const filteredRecognitions = useMemo(() => {
    return recognitions.filter(r => {
      if (search && !r.contractNumber?.toLowerCase().includes(search.toLowerCase()) &&
          !r.projectName?.toLowerCase().includes(search.toLowerCase()) &&
          !r.clientName?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (projectFilter && r.projectId !== projectFilter) return false;
      if (methodFilter && r.method !== methodFilter) return false;
      return true;
    });
  }, [recognitions, search, statusFilter, projectFilter, methodFilter]);

  // Filter journal entries for revenue
  const revenueJournals = useMemo(() => 
    journalEntries.filter(j => 
      j.sourceType === 'revenue_recognition' || 
      j.narration?.toLowerCase().includes('revenue') ||
      j.lines?.some((l: any) => l.accountType === 'income' || l.accountType === 'liability')
    ), [journalEntries]
  );

  // Compute dashboard metrics
  const metrics = useMemo(() => {
    const totalContractValue = recognitions.reduce((sum, r) => sum + (r.totalContractValueMinor || 0), 0);
    const recognizedToDate = recognitions.reduce((sum, r) => sum + (r.recognizedToDateMinor || 0), 0);
    const deferredRevenue = recognitions.reduce((sum, r) => sum + (r.deferredRevenueMinor || 0), 0);
    const unbilledReceivables = recognitions.reduce((sum, r) => sum + (r.unbilledReceivablesMinor || 0), 0);
    const pendingRecognition = recognitions
      .filter(r => r.status === 'pending' || r.status === 'scheduled')
      .reduce((sum, r) => sum + (r.totalContractValueMinor || 0) - (r.recognizedToDateMinor || 0), 0);
    
    return {
      totalContractValue,
      recognizedToDate,
      deferredRevenue,
      unbilledReceivables,
      pendingRecognition,
      recognitionRate: totalContractValue > 0 ? (recognizedToDate / totalContractValue * 100).toFixed(1) : 0,
    };
  }, [recognitions]);

  const monthlyRecognition = useMemo(() => {
    const months: Record<string, { recognized: number; deferred: number }> = {};
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toISOString().slice(0, 7);
      months[key] = { recognized: 0, deferred: 0 };
    }
    
    recognitions.forEach(r => {
      (r.schedule || []).forEach((item: any) => {
        if (item.recognizedAt) {
          const key = new Date(item.recognizedAt).toISOString().slice(0, 7);
          if (months[key]) months[key].recognized += item.amountMinor || 0;
        } else if (item.recognitionDate) {
          const key = new Date(item.recognitionDate).toISOString().slice(0, 7);
          if (months[key]) months[key].deferred += item.amountMinor || 0;
        }
      });
    });
    
    return Object.entries(months).map(([month, data]) => ({
      month,
      label: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      recognized: data.recognized,
      deferred: data.deferred,
    }));
  }, [recognitions]);

  const upcomingSchedule = useMemo(() => {
    const items: any[] = [];
    recognitions.forEach(r => {
      (r.schedule || []).forEach((item: any) => {
        if (item.status === 'pending' || item.status === 'scheduled') {
          items.push({
            ...item,
            contractNumber: r.contractNumber,
            projectName: r.projectName,
            clientName: r.clientName,
            contractId: r.contractId,
          });
        }
      });
    });
    return items
      .sort((a, b) => new Date(a.recognitionDate).getTime() - new Date(b.recognitionDate).getTime())
      .slice(0, 20);
  }, [recognitions]);

  const overdueSchedule = useMemo(() => {
    const now = new Date();
    const items: any[] = [];
    recognitions.forEach(r => {
      (r.schedule || []).forEach((item: any) => {
        if ((item.status === 'pending' || item.status === 'scheduled') && new Date(item.recognitionDate) < now) {
          items.push({
            ...item,
            contractNumber: r.contractNumber,
            projectName: r.projectName,
            clientName: r.clientName,
            daysOverdue: Math.floor((now.getTime() - new Date(item.recognitionDate).getTime()) / (1000 * 60 * 60 * 24)),
          });
        }
      });
    });
    return items.sort((a, b) => b.daysOverdue - a.daysOverdue).slice(0, 10);
  }, [recognitions]);

  const recognitionByMethod = useMemo(() => {
    const byMethod: Record<string, number> = {};
    (recognitions || []).forEach(r => {
      if (r.method) {
        byMethod[r.method] = (byMethod[r.method] || 0) + (r.recognizedToDateMinor || 0);
      }
    });
    return Object.entries(byMethod).sort((a, b) => b[1] - a[1]);
  }, [recognitions]);

  const handleRunRecognition = async (recognitionId: string) => {
    if (!window.confirm('Run revenue recognition for this contract?')) return;
    setRunning(true);
    try {
      const response = await fetch(`/api/v2/services-projects/revenue-recognition/${recognitionId}/run`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to run recognition');
      toast.success('Revenue recognition processed');
    } catch (err) {
      toast.error(errorText(err, 'Failed to run recognition'));
    } finally {
      setRunning(false);
    }
  };

  const handleRecognizeItem = async (recognitionId: string, itemId: string) => {
    if (!window.confirm('Recognize this revenue item?')) return;
    try {
      const response = await fetch(`/api/v2/services-projects/revenue-recognition/${recognitionId}/recognize/${itemId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed');
      toast.success('Revenue recognized and journal posted');
    } catch (err) {
      toast.error(errorText(err, 'Failed to recognize revenue'));
    }
  };

  const handleCreateSchedule = async () => {
    // Modal would open here
    toast.success('Schedule creation - use contract module to create schedules');
  };

  const isLoading = recognitionsLoading;

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={ArrowLeftRight}
        eyebrow="Projects & Services"
        title="Revenue Recognition"
        description={<>ASC 606 / IFRS 15 compliant revenue recognition schedules and journal posting</>}
        actions={
          <><div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveTab('overview')}>
                    <BarChart3 className="w-4 h-4 mr-2" /> Overview
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('schedules')}>
                    <Calendar className="w-4 h-4 mr-2" /> Schedules
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('entries')}>
                    <DollarSign className="w-4 h-4 mr-2" /> Entries
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('journals')}>
                    <FileText className="w-4 h-4 mr-2" /> Journals
                  </Button>
                  <Button onClick={handleCreateSchedule}>
                    <Plus className="w-4 h-4 mr-2" /> New Schedule
                  </Button>
                </div></>
        }
      />

      {/* Overview Dashboard */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Contract Value</p>
                    <p className="text-2xl font-bold">{formatINR(metrics.totalContractValue)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Recognized Revenue</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatINR(metrics.recognizedToDate)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Deferred Revenue</p>
                    <p className="text-2xl font-bold text-blue-600">{formatINR(metrics.deferredRevenue)}</p>
                  </div>
                  <Shield className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unbilled Receivables</p>
                    <p className="text-2xl font-bold text-amber-600">{formatINR(metrics.unbilledReceivables)}</p>
                  </div>
                  <Zap className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Recognition Rate</p>
                    <p className="text-2xl font-bold">{metrics.recognitionRate}%</p>
                  </div>
                  <PieChart className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Monthly Recognition Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64 space-y-2">
                  {monthlyRecognition.map((m, i) => (
                    <div key={m.month} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-gray-500">{m.label}</span>
                      <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded relative overflow-hidden">
                        <div className="absolute top-0 bottom-0 left-0 bg-emerald-500 rounded-l" style={{ width: monthlyRecognition[i]?.recognized ? (m.recognized / Math.max(1, m.recognized + m.deferred) * 100) : 0 + '%' }} />
                        <div className="absolute top-0 bottom-0 right-0 bg-amber-500 rounded-r" style={{ width: monthlyRecognition[i]?.deferred ? (m.deferred / Math.max(1, m.recognized + m.deferred) * 100) : 0 + '%' }} />
                      </div>
                      <span className="w-24 text-sm text-right font-mono text-emerald-600">{formatINR(m.recognized)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Recognition by Method</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64 space-y-2">
                  {recognitionByMethod.map(([method, amount]) => (
                    <div key={method} className="flex items-center gap-3">
                      <span className="w-40 text-sm capitalize">{(method || '').replace('_', ' ')}</span>
                      <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded relative overflow-hidden">
                        <div className="absolute top-0 bottom-0 left-0 bg-blue-500 rounded" style={{ width: `${(amount / (metrics.totalContractValue || 1) * 100).toFixed(1)}%` }} />
                      </div>
                      <span className="w-24 text-sm text-right font-mono">{formatINR(amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts */}
          <div className="grid gap-4 lg:grid-cols-2">
            {overdueSchedule.length > 0 && (
              <Card className="border-l-4 border-red-500">
                <CardHeader className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <CardTitle className="text-red-600">Overdue Recognition Items ({overdueSchedule.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {overdueSchedule.map((item, i) => (
                      <div key={i} className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{item.contractNumber}</p>
                            <p className="text-sm text-gray-500">{item.projectName} • {formatINR(item.amountMinor)}</p>
                          </div>
                          <span className="text-red-600 font-medium">{item.daysOverdue} days overdue</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {upcomingSchedule.length > 0 && (
              <Card>
                <CardHeader className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  <CardTitle>Upcoming Recognition ({upcomingSchedule.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {upcomingSchedule.map((item, i) => (
                      <div key={i} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{item.contractNumber}</p>
                          <p className="text-sm text-gray-500">{item.projectName} • {formatINR(item.amountMinor)}</p>
                        </div>
                        <span className="text-blue-600 font-medium">{new Date(item.recognitionDate).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Schedules Tab */}
      {activeTab === 'schedules' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                  <Input placeholder="Search contract, project, client..." value={search} onChange={e => setSearch(e.target.value)} leftIcon={<Search className="w-4 h-4" />} />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...RECOGNITION_STATUS.map(s => ({ value: s.value, label: s.label }))]} />
                </div>
                <div className="min-w-[180px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Method</label>
                  <Select value={methodFilter} onChange={e => setMethodFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...RECOGNITION_METHODS.map(m => ({ value: m.value, label: m.label }))]} />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
                  <Select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...projects.map(p => ({ value: p._id, label: p.name }))]} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedules Table */}
          {isLoading ? (
            <Card><CardContent className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></CardContent></Card>
          ) : filteredRecognitions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No recognition schedules</h3>
                <p className="text-gray-500 mb-4">Create schedules from contracts or run recognition.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contract</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Contract Value</TableHead>
                      <TableHead className="text-right">Recognized</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead>Next Recognition</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-48">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecognitions.map((r: any) => {
                      const pending = (r.totalContractValueMinor || 0) - (r.recognizedToDateMinor || 0);
                      const nextItem = r.schedule?.find((s: any) => s.status === 'pending' || s.status === 'scheduled');
                      const statusConfig = getStatusConfig(r.status);
                      return (
                        <TableRow key={r._id}>
                          <TableCell className="font-mono font-medium">{r.contractNumber}</TableCell>
                          <TableCell>{r.projectName || '—'}</TableCell>
                          <TableCell>{r.clientName || '—'}</TableCell>
                          <TableCell><Badge variant="secondary">{r.method?.replace('_', ' ') || '—'}</Badge></TableCell>
                          <TableCell className="text-right font-mono">{formatINR(r.totalContractValueMinor || 0)}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{formatINR(r.recognizedToDateMinor || 0)}</TableCell>
                          <TableCell className="text-right font-mono text-amber-600">{formatINR(pending)}</TableCell>
                          <TableCell>
                            {nextItem ? new Date(nextItem.recognitionDate).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell><Badge className={statusConfig.color}>{statusConfig.label}</Badge></TableCell>
                          <TableCell className="w-48">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleRunRecognition(r._id)} title="Run Recognition" disabled={running} className="text-blue-500">
                                <Zap className="w-4 h-4" />
                              </Button>
                              {r.schedule?.some((s: any) => s.status === 'pending' || s.status === 'scheduled') && (
                                <Button variant="secondary" size="sm" onClick={() => handleRunRecognition(r._id)} disabled={running}>
                                  <Zap className="w-3.5 h-3.5 mr-1" /> Run
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Entries Tab */}
      {activeTab === 'entries' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recognition Entries</CardTitle>
                <Button onClick={() => handleRunRecognition('all')}><Zap className="w-4 h-4 mr-2" /> Run All Due</Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredRecognitions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No schedules found</div>
              ) : (
                <div className="space-y-3">
                  {filteredRecognitions.flatMap((r: any) => 
                    (r.schedule || []).map((item: any, idx: number) => {
                      const statusConfig = getStatusConfig(item.status);
                      const isOverdue = new Date(item.recognitionDate) < new Date() && (item.status === 'pending' || item.status === 'scheduled');
                      return (
                        <Card key={`${r._id}-${idx}`} className={isOverdue ? 'border-l-4 border-red-500' : ''}>
                          <CardContent className="p-4">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <div className="flex items-center gap-4 flex-1">
                                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                                <div>
                                  <p className="font-medium">{r.contractNumber}</p>
                                  <p className="text-sm text-gray-500">{r.projectName} • {r.clientName}</p>
                                </div>
                                <div className="text-sm text-gray-500">
                                  {formatINR(item.amountMinor)} • {new Date(item.recognitionDate).toLocaleDateString()}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isOverdue && <span title="Overdue"><AlertCircle className="w-5 h-5 text-red-500" /></span>}
                                {(item.status === 'pending' || item.status === 'scheduled') && (
                                  <Button size="sm" onClick={() => handleRecognizeItem(r._id, item.id || idx)}>
                                    <Zap className="w-4 h-4 mr-1" /> Recognize
                                  </Button>
                                )}
                                {item.status === 'recognized' && item.journalEntryId && (
                                  <Button variant="ghost" size="icon" className="text-blue-500" title="View Journal">
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Journals Tab */}
      {activeTab === 'journals' && (
        <Card>
          <CardHeader><CardTitle>Revenue Recognition Journals</CardTitle></CardHeader>
          <CardContent>
            {revenueJournals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>No revenue recognition journals posted yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Journal #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Contract</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-40">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {revenueJournals.map((j: any) => {
                      const statusConfig = getJournalStatusConfig(j.status);
                      const debit = j.lines?.filter((l: any) => l.debit > 0).reduce((sum: number, l: any) => sum + l.debit, 0) || 0;
                      const credit = j.lines?.filter((l: any) => l.credit > 0).reduce((sum: number, l: any) => sum + l.credit, 0) || 0;
                      return (
                        <TableRow key={j._id}>
                          <TableCell className="font-mono">{j.journalNumber || j._id.slice(-8)}</TableCell>
                          <TableCell>{j.date ? new Date(j.date).toLocaleDateString() : '—'}</TableCell>
                          <TableCell>{j.sourceId || '—'}</TableCell>
                          <TableCell className="max-w-xs truncate">{j.narration || 'Revenue recognition entry'}</TableCell>
                          <TableCell className="text-right font-mono">{formatINR(debit)}</TableCell>
                          <TableCell className="text-right font-mono">{formatINR(credit)}</TableCell>
                          <TableCell><Badge className={statusConfig.color}>{statusConfig.label}</Badge></TableCell>
                          <TableCell className="w-40">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" title="View"><Eye className="w-4 h-4" /></Button>
                              {j.status === 'posted' && (
                                <Button variant="ghost" size="icon" className="text-red-500" title="Reverse">
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}